import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
  Badge,
  Box,
  Button,
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogPopup,
  DialogTitle,
  Icon,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectValue,
  Text,
  toastManager,
} from "@salestrak/ui";
import { EntityTag } from "#app/components/entity";
import { useDebouncedValue } from "#app/lib/hooks/use-debounced-value";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import {
  HeaderLabel,
  PanelCard,
  ScreenHeader,
  TableHeaderRow,
  TableRow,
} from "./admin-ui";
import { useAdminFetch } from "./admin-shared";
import type {
  AdminWorkspace,
  Superuser,
  WorkspaceRef,
} from "./use-admin-data";

type SearchResult = { userId: string; name: string; email: string };

function WorkspaceChips({ workspaces }: { workspaces: WorkspaceRef[] }) {
  if (!workspaces.length) {
    return (
      <Text size="xs" variant="muted">
        —
      </Text>
    );
  }
  const shown = workspaces.slice(0, 3);
  const extra = workspaces.length - shown.length;
  return (
    <Box direction="row" align="center" gap="xs" wrap>
      {shown.map((w, i) => (
        <Badge key={`${w.id}-${i}`} variant="outline" size="sm">
          <Icon name="layout-grid" size="xs" />
          {w.name}
        </Badge>
      ))}
      {extra > 0 ? (
        <Text size="xs" variant="muted" mono>
          +{extra}
        </Text>
      ) : null}
    </Box>
  );
}

export function SuperusersScreen({
  superusers,
  loading,
  memberships,
  workspaces,
  refetch,
}: {
  superusers: Superuser[];
  loading: boolean;
  memberships: Record<string, WorkspaceRef[]>;
  workspaces: AdminWorkspace[];
  refetch: () => void;
}) {
  const { loginFetch } = useAdminFetch();

  const [grantOpen, setGrantOpen] = useState(false);
  const [search, setSearch] = useState("");
  const debounced = useDebouncedValue(search, 300);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<Superuser | null>(null);
  const [workspaceFilter, setWorkspaceFilter] = useState<string | null>(null);

  // Debounced user search inside the grant dialog.
  useEffect(() => {
    if (!grantOpen || debounced.trim().length < 2) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setSearching(true);
    (async () => {
      try {
        const res = await loginFetch(
          `/oAuth/users/search?query=${encodeURIComponent(debounced)}`,
        );
        const body = await res.json();
        if (!cancelled) setResults(body.users ?? []);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debounced, grantOpen, loginFetch]);

  const setSuperuser = useCallback(
    async (userId: string, value: boolean, name: string) => {
      try {
        const res = await loginFetch("/oAuth/superuser", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId, superuser: value }),
        });
        if (!res.ok) throw new Error();
        refetch();
        toastManager.add({
          title: value
            ? `${name || "User"} granted superuser`
            : `${name || "User"} removed as superuser`,
          type: "success",
        });
      } catch (err) {
        toastMutationError(
          err,
          value ? "Failed to grant superuser" : "Failed to remove superuser",
        );
      }
    },
    [loginFetch, refetch],
  );

  const filtered = useMemo(() => {
    if (!workspaceFilter) return superusers;
    return superusers.filter((u) =>
      (memberships[u.userId] ?? []).some((w) => w.id === workspaceFilter),
    );
  }, [superusers, memberships, workspaceFilter]);

  const filterOptions: { value: string; label: string }[] = useMemo(
    () => [
      { value: "__all__", label: "All workspaces" },
      ...workspaces.map((w) => ({ value: w.id, label: w.name })),
    ],
    [workspaces],
  );

  return (
    <Box fill="flex" overflow="auto">
      <ScreenHeader
        title="Superusers"
        meta={`${superusers.length} with platform access`}
        actions={
          <>
            {/* Base UI's Select is value-based (no `{value,label}` Option). */}
            <Select
              value={workspaceFilter ?? "__all__"}
              onValueChange={(next: string | null) =>
                setWorkspaceFilter(!next || next === "__all__" ? null : next)
              }
            >
              <SelectTrigger size="sm">
                <SelectValue placeholder="All workspaces">
                  {(value: string) =>
                    filterOptions.find((o) => o.value === value)?.label ??
                    "All workspaces"
                  }
                </SelectValue>
              </SelectTrigger>
              <SelectPopup>
                {filterOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    <Text size="sm">{o.label}</Text>
                  </SelectItem>
                ))}
              </SelectPopup>
            </Select>
            <Button
              size="sm"
              onClick={() => {
                setSearch("");
                setResults([]);
                setGrantOpen(true);
              }}
            >
              <Icon name="user-plus" size="sm" />
              Grant superuser
            </Button>
          </>
        }
      />

      <Box paddingX="lg" paddingBottom="xl" paddingTop="sm">
        <PanelCard>
          <TableHeaderRow>
            <HeaderLabel fill>User</HeaderLabel>
            <HeaderLabel fill>Workspaces</HeaderLabel>
            <HeaderLabel width={44}>{""}</HeaderLabel>
          </TableHeaderRow>

          {!loading && filtered.length === 0 ? (
            <Box paddingX="md" paddingY="lg" align="center">
              <Text size="sm" variant="muted">
                No superusers found
              </Text>
            </Box>
          ) : null}

          {filtered.map((u, index) => (
            <TableRow key={`${u.userId}-${index}`} last={index === filtered.length - 1}>
              <Box fill="flex" minWidth={0}>
                <EntityTag
                  entity={{
                    kind: "user",
                    id: u.userId,
                    user: { name: u.name, email: u.email },
                  }}
                  subtitle
                  interactive={false}
                  size="sm"
                />
              </Box>
              <Box fill="flex">
                <WorkspaceChips workspaces={memberships[u.userId] ?? []} />
              </Box>
              <Box width={44} align="end">
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Remove superuser"
                  onClick={() => setRemoveTarget(u)}
                >
                  <Icon name="x" size="sm" color="destructive" />
                </Button>
              </Box>
            </TableRow>
          ))}
        </PanelCard>

        <Box direction="row" align="center" gap="xs" paddingTop="sm">
          <Icon name="info" size="xs" color="muted" />
          <Text size="xs" variant="muted">
            Superuser access grants platform-wide administrative control across
            every workspace.
          </Text>
        </Box>
      </Box>

      {/* Grant dialog */}
      <Dialog open={grantOpen} onOpenChange={(o) => !o && setGrantOpen(false)}>
        <DialogPopup>
          <DialogHeader>
            <DialogTitle>Grant superuser</DialogTitle>
            <DialogDescription>
              Search by name or email to grant platform access.
            </DialogDescription>
          </DialogHeader>
          <Box paddingX="lg">
            <InputGroup>
              <InputGroupAddon>
                <Icon name="search" />
              </InputGroupAddon>
              <InputGroupInput
                type="search"
                value={search}
                onValueChange={setSearch}
                placeholder="Search users…"
              />
            </InputGroup>
          </Box>
          <Box paddingX="lg" paddingTop="sm" paddingBottom="lg" gap="2xs">
            {searching ? (
              <Text size="sm" variant="muted">
                Searching…
              </Text>
            ) : null}
            {!searching && debounced.trim().length >= 2 && results.length === 0 ? (
              <Text size="sm" variant="muted">
                No users found
              </Text>
            ) : null}
            {results.map((r, i) => (
              <Box
                key={`${r.userId}-${i}`}
                direction="row"
                align="center"
                gap="sm"
                paddingX="xs"
                paddingY="xs"
                rounded="md"
              >
                <Box fill="flex" minWidth={0}>
                  <EntityTag
                    entity={{
                      kind: "user",
                      id: r.userId,
                      user: { name: r.name, email: r.email },
                    }}
                    subtitle
                    interactive={false}
                    size="sm"
                  />
                </Box>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSuperuser(r.userId, true, r.name);
                    setGrantOpen(false);
                  }}
                >
                  Grant
                </Button>
              </Box>
            ))}
          </Box>
        </DialogPopup>
      </Dialog>

      {/* Remove confirm */}
      <AlertDialog
        open={!!removeTarget}
        onOpenChange={(o) => !o && setRemoveTarget(null)}
      >
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove superuser</AlertDialogTitle>
            <AlertDialogDescription>
              {`Remove superuser access from ${removeTarget?.name || removeTarget?.email || "this user"}?`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" />}>
              Cancel
            </AlertDialogClose>
            <Button
              variant="destructive"
              onClick={() => {
                if (removeTarget)
                  setSuperuser(removeTarget.userId, false, removeTarget.name);
                setRemoveTarget(null);
              }}
            >
              Remove
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </Box>
  );
}
